import { useLayoutEffect, useState } from "react";

import { isEmpty, orderBy } from "lodash/fp";
import { DateTime } from "luxon";

import {
  BulletedList,
  BulletedListItem,
} from "@app/analytics/components/reports/styles";
import type { TimelineEntityGroup } from "@app/analytics/TopicReport/types";
import { getRelativeDisplayDate } from "@app/analytics/utils";

import AutoAnimateContainer from "@rui/AutoAnimateContainer";
import { Column, DesktopOnlyColumn, Row } from "@rui/Stack";

import { Connector, Details, Indicator, Text, Wrapper } from "./styles";

interface TimelineItemProps extends TimelineEntityGroup {
  active?: boolean;
  forceExpand?: boolean;
  onClick: (id: TimelineEntityGroup["id"]) => void;
}

const getFormattedDate = (date: DateTime) => {
  const daysBeforeNow = Math.ceil(DateTime.now().diff(date).as("days"));
  if (daysBeforeNow < 3) {
    return getRelativeDisplayDate(date);
  }
  return date.toLocaleString({ month: "short", day: "numeric" });
};

const TimelineItem = ({
  active = false,
  date,
  forceExpand = false,
  id,
  update,
  entities,
  onClick,
}: TimelineItemProps) => {
  const [expanded, setExpanded] = useState(active);

  const sortedEntities = orderBy("rank", "desc", entities);

  useLayoutEffect(() => {
    if (!expanded && (forceExpand || active)) {
      setExpanded(true);
    } else if (!active && expanded) {
      setExpanded(false);
    }
  }, [active, forceExpand]);

  const handleClick = () => {
    if (active) {
      setExpanded((e) => !e);
    } else {
      onClick(id);
      setExpanded(true);
    }
  };

  return (
    <Wrapper
      direction={{ xxs: "column", mobile: "row" }}
      spacing={{ xxs: 1, mobile: 2 }}
      onClick={handleClick}
    >
      <Row spacing={1} minWidth="100px">
        <DesktopOnlyColumn height="100%" variant="center">
          <Connector className="timeline-item__connector--top" />
          <Indicator active={active} />
          <Connector className="timeline-item__connector--bottom" />
        </DesktopOnlyColumn>
        <Text bold={active} compact paddingTop={1}>
          {getFormattedDate(date)}
        </Text>
      </Row>
      <Details active={active} spacing={1}>
        {update && (
          <Row overflow="hidden" width="100%">
            <Text noWrap={!expanded}>
              <Text bold component="span">
                Daily Update:
              </Text>{" "}
              {update.text}
            </Text>
          </Row>
        )}
        {!isEmpty(entities) && (
          <Column>
            <Row>
              <Text bold>Takeaways ({entities.length}):</Text>
              {!expanded && <Text>...</Text>}
            </Row>
            <AutoAnimateContainer show={expanded}>
              <BulletedList>
                {sortedEntities.map((entity) => (
                  <BulletedListItem key={entity.entityId}>
                    <Text>{entity.text}</Text>
                  </BulletedListItem>
                ))}
              </BulletedList>
            </AutoAnimateContainer>
          </Column>
        )}
      </Details>
    </Wrapper>
  );
};

export default TimelineItem;
